import React, { useState } from 'react';
import { CloudArrowDown, Check, Spinner, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../utils/api';

/**
 * SaveOfflineButton — prefetches every endpoint a producer is likely to
 * need offline for a single show, so the service worker caches them in
 * one batch. Producers tap this once with WiFi at the venue gate; the
 * Manage Entries / Class Entries / Draws / Results / Spot Reservations
 * pages then work in airplane mode for the next 24 hours.
 *
 * The service worker (public/sw.js) automatically caches successful GETs,
 * so this component just *triggers* the requests; no new caching logic.
 *
 * Props:
 *   show: full show object (must include id + classes[])
 *   className: optional Tailwind extra
 */
export const SaveOfflineButton = ({ show, className = '' }) => {
  const [status, setStatus] = useState('idle'); // idle | running | done | error
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  if (!show?.id) return null;

  const buildPrefetchTargets = () => {
    const showId = show.id;
    const classes = show.classes || [];
    const targets = [
      `/shows/${showId}`,
      `/shows/${showId}/all-entries`,
      `/refunds/show/${showId}`,
      `/spot-reservations?show_id=${showId}`,
      `/shows/${showId}/extra-locations`,
      `/disputes?show_id=${showId}`,
    ];
    classes.forEach((c) => {
      if (!c?.id) return;
      targets.push(`/shows/${showId}/classes/${c.id}/entries`);
      targets.push(`/results/${c.id}`);
    });
    return targets;
  };

  const handleSave = async () => {
    if (status === 'running') return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      toast.error("You're offline — connect to WiFi/cell first.");
      return;
    }
    setStatus('running');
    const targets = buildPrefetchTargets();
    setProgress({ done: 0, total: targets.length });

    let done = 0;
    const results = await Promise.allSettled(
      targets.map((path) =>
        api.get(path).finally(() => {
          done += 1;
          setProgress((p) => ({ ...p, done }));
        })
      )
    );
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.length - ok;

    if (ok === 0) {
      setStatus('error');
      toast.error('Could not save show offline — check your connection');
      return;
    }
    setStatus('done');
    toast.success(
      failed > 0
        ? `Saved ${ok} pages for offline (${failed} failed — likely empty sections)`
        : `Show saved for offline · ${ok} pages cached`
    );
    setTimeout(() => setStatus('idle'), 3000);
  };

  const baseClass =
    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors';
  if (status === 'running') {
    const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
    return (
      <button
        disabled
        className={`${baseClass} bg-[#0096FF]/10 border border-[#0096FF]/30 text-[#0096FF] ${className}`}
        data-testid="save-offline-progress"
      >
        <Spinner size={12} weight="bold" className="animate-spin" />
        Caching {progress.done}/{progress.total} ({pct}%)
      </button>
    );
  }
  if (status === 'done') {
    return (
      <button
        disabled
        className={`${baseClass} bg-[#34C759]/10 border border-[#34C759]/30 text-[#34C759] ${className}`}
        data-testid="save-offline-done"
      >
        <Check size={12} weight="bold" /> Saved for offline
      </button>
    );
  }
  if (status === 'error') {
    return (
      <button
        onClick={handleSave}
        className={`${baseClass} bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/20 ${className}`}
        data-testid="save-offline-retry"
      >
        <X size={12} weight="bold" /> Failed — retry
      </button>
    );
  }
  return (
    <button
      onClick={handleSave}
      className={`${baseClass} bg-[#0096FF]/10 border border-[#0096FF]/30 text-[#0096FF] hover:bg-[#0096FF]/20 ${className}`}
      data-testid="save-offline-btn"
      title="Cache this show's pages so they work in airplane mode for 24h"
    >
      <CloudArrowDown size={12} weight="bold" /> Save for offline
    </button>
  );
};
